// ── FSA-578 INTAKE: when a row may say "forwarded", and when a file is a repeat ──
//
// Two rules, two callers on opposite sides of the wire — `api/box.js` (the Box
// sync) and `src/App.jsx` (the manual uploader). CommonJS in `src/` for the same
// reason as src/campaign578.js: a Vercel function can require it without a build,
// and CRA forbids `src/` importing from `api/`.
//
// 1. A ROW IS "FORWARDED" ONLY AFTER THE EMAIL WAS ACCEPTED.
//    The Box sync used to INSERT `forwarded_to_mandy: true` before it sent the
//    email, and a failed send only reached `summary.errors` — so the 578 board
//    showed "sent" for a 578 Mandy never received, and the "Send 578" button that
//    would have fixed it was hidden. Rows are now written NOT forwarded, and the
//    forwarded flag is set by a PATCH that runs only when the send reports success.
//    `sendNotificationEmail` RESOLVES `{sent:false}` on a Resend rejection rather
//    than throwing, so the caller must test `.sent`, never just "did it throw".
//
// 2. THE SAME BYTES ARE THE SAME 578.
//    `farmer_578s` has no uniqueness on content, and the manual uploader checked
//    nothing, so the same PDF uploaded twice became two rows — and a duplicate row
//    is what makes the acreage audit read every acre at 2.00x. A SHA-256 of the PDF
//    bytes (`content_sha256`, sql/097) identifies an identical file regardless of
//    its filename, its county or whether the name says "UPDATED". A genuinely
//    revised 578 is a different PDF, so it has different bytes and still flows.
//
//    The column may not exist yet (the migration is applied separately). Every
//    path treats "column does not exist" as "no fingerprint available" and behaves
//    exactly as it did before — never as an error that blocks an upload.

const SHA256_HEX = /^[0-9a-f]{64}$/;

function isSha256Hex(s) {
  return typeof s === "string" && SHA256_HEX.test(s);
}

// The row a writer INSERTS, before any email. Never forwarded. An unmatched 578
// keeps `pending_match` (it still needs a farmer); a matched one is `matched`.
function initialForwardState({ matched }) {
  return {
    forwarded_to_mandy: false,
    forwarded_at: null,
    status: matched ? "matched" : "pending_match",
  };
}

// The PATCH applied once the send SUCCEEDED. `sendResult` is what the mailer
// returned; anything but an explicit `sent === true` returns null — no patch.
function forwardedPatch({ matched, sendResult, at }) {
  if (!sendResult || sendResult.sent !== true) return null;
  return {
    forwarded_to_mandy: true,
    forwarded_at: at || new Date().toISOString(),
    // Forwarding does not resolve the farmer match: an unmatched 578 that was
    // emailed still needs assigning, so it keeps the status that says so.
    status: matched ? "forwarded" : "pending_match",
  };
}

// PostgREST filter for "a row with these exact bytes already exists".
// Returns null for anything that is not a SHA-256 hex digest, so a malformed or
// missing hash can never become a filter that matches every row.
function contentHashFilter(sha) {
  return isSha256Hex(sha) ? `content_sha256=eq.${sha}` : null;
}

// Does this error mean "the content_sha256 column is not there yet"?
// 42703 = undefined column (a filter/select naming it); PGRST204 = the insert
// body names a column the schema cache does not know. Both must mention the
// column, so an unrelated 42703 is not swallowed as "no fingerprint".
function isMissingHashColumn(err) {
  const s = String((err && (err.message || err)) || "");
  if (!/content_sha256/.test(s)) return false;
  return /42703|PGRST204|does not exist|schema cache/i.test(s);
}

// A row without the fingerprint, for the retry when the column is absent.
function withoutHash(row) {
  const out = { ...row };
  delete out.content_sha256;
  return out;
}

// What the uploader is told. Names the farmer and the date the first copy landed.
function duplicateMessage(existing, farmerName) {
  const who = farmerName || existing?.extracted_name || "an unmatched farmer";
  const when = existing?.received_at ? String(existing.received_at).slice(0, 10) : "an earlier date";
  return `This exact file is already on file for ${who}, ${when}. Not saved again.`;
}


// 3. EVERY ROUTE EMAILS MANDY, ONCE, AND NEVER FOR A HISTORICAL 578.
//    Kevin, 2026-10-19: one email per 578, whatever door it came through. Box and the
//    manual uploader already sent; a 578 that arrived through the Dropbox folder sync or
//    the document hub landed un-forwarded and waited for somebody to press "Send 578".
//    It now sends on arrival — but only for a 578 the intake actually FILED (decision "import")
//    whose program year names a LIVE campaign. A 2024 print dropped in a folder is
//    filed for the record and is not news to Mandy; that is the same reason the Box sync
//    refuses a wrong-year or stale file. `acceptedYears` is the caller's
//    campaign578.acceptedProgramYears(), never a list typed here.
function emailOnArrival({ decision, year, acceptedYears }) {
  if (!decision || decision.decision !== "import") return false;
  const y = Number(year != null ? year : decision.year);
  if (!Number.isInteger(y)) return false;
  return (acceptedYears || []).map(Number).includes(y);
}

// 4. A 578 THAT ARRIVES IS QUEUED TO BE READ ONCE (sql/104, src/fsa578Lines.js).
//    `lines_status: "pending"` is written ONLY by an arrival — never by a migration —
//    so the rows already on file are not swept into an Opus read by a deploy.
function arrivalLinesState() {
  return { lines_status: "pending" };
}
// Columns a 578 insert may carry that a later migration adds. "Column does not exist"
// on any of them means "not there yet": the insert is retried without them — the old
// behaviour, never an error that blocks a 578 from being filed.
const ARRIVAL_COLUMNS = ["content_sha256", "lines_status"];
function isMissingArrivalColumn(err) {
  const s = String((err && (err.message || err)) || "");
  if (!ARRIVAL_COLUMNS.some((c) => s.includes(c))) return false;
  return /42703|PGRST204|does not exist|schema cache/i.test(s);
}
function withoutArrivalColumns(row) {
  const out = { ...row };
  for (const c of ARRIVAL_COLUMNS) delete out[c];
  return out;
}

module.exports = {
  arrivalLinesState,
  isMissingArrivalColumn,
  withoutArrivalColumns,
  emailOnArrival,
  isSha256Hex,
  initialForwardState,
  forwardedPatch,
  contentHashFilter,
  isMissingHashColumn,
  withoutHash,
  duplicateMessage,
};
